import {useContext, useState, useEffect, useRef} from "react";
import {ImageContext} from "../../../../shared/tools/image/imageContext";
import {Container} from "@mui/material";
import {
  AssemblyMainField,
  AssemblyPreview,
  AssemblyBaseSelector,
  AssemblyAreaSelector,
  AssemblyKitSelector,
  AssemblyFooter,
} from "../";
import classNames from "classnames";
import {useAssemblyBricksApi} from "../../api";
import "./AssemblyView.scss";
import {IPaletteType} from "../../../art/components/ArtCreateView/Art";
import {useLangContext} from "shared/features/Language";
import {useConsumerTokenStorage} from "../../../../shared/tools";
import {MobileTutorialComponent, TutorialLayerComponent} from "../TutorialLayer";

interface IProps {
  sessionId: string;
}

export const AssemblyView = ({sessionId}: IProps) => {
  const consumerTokenStorage = useConsumerTokenStorage();
  const {textData} = useLangContext();
  const {brickSave, resumeArt, skipStepsArt} = useAssemblyBricksApi();

  const viewMainRef = useRef<HTMLDivElement>(null);
  const [viewMainTop, setViewMainTop] = useState<number>(207);
  const [viewMainBottom, setViewMainBottom] = useState<number>(0);

  const [kitsQuantity, setKitsQuantity] = useState(0);
  const [activeKit, setActiveKit] = useState(0);
  const [activeBase, setActiveBase] = useState(0);
  const [activeArea, setActiveArea] = useState(0);
  const [areaIndex, setAreaIndex] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [activeBrick, setActiveBrick] = useState(0);
  const [completedBricks, setCompletedBricks] = useState<number[]>(new Array(16).fill(0));
  const [completedBrick, setCompletedBrick] = useState(0);
  const [parts, setParts] = useState([]);
  const [unsavedStatus, setUnsavedStatus] = useState(false);
  const [tutorialVisible, setTutorialVisible] = useState(true);
  const [mobileTutorialStep, setMobileTutorialStep] = useState(2);
  const {schema, setSchema} = useContext(ImageContext);
  const [skip, setSkip] = useState(0);

  const paletteId: IPaletteType = 'default';

  const saveData = async (bricks) => {
    const stepNumber = bricks.reduce((s, a) => (s + a), 0);
    try {
      await brickSave({
        code: sessionId,
        step: bricks[areaIndex],
        stepIndex: areaIndex,
        stepNumber
      });
    } catch (e) {
      console.error(e);
    }
  }

  const resumeData = async () => {
    try {
      const res = await resumeArt(sessionId);
      const _schema = JSON.parse(res.stepsData);
      const _completedBricks = JSON.parse(res?.lastStep || "[]");
      const _completedBrick = _completedBricks[activeArea];
      setSchema(_schema);
      if (_completedBricks && _completedBricks.length) {
        setCompletedBricks(_completedBricks);
      }
      setCompletedBrick(_completedBrick);
      setSkip(0);
      consumerTokenStorage.set(JSON.stringify(res.sessionToken));
    } catch (e) {
      console.error(e);
    }
  }

  const stepBackHandler = async () => {
    const stepsSkip = skip + 1;
    try {
      const res = await skipStepsArt(sessionId, stepsSkip);
      const _schema = JSON.parse(res.stepsData);
      const _completedBricks = JSON.parse(res?.lastStep || "[]");
      const _completedBrick = _completedBricks[activeArea];
      setSchema(_schema);
      if (_completedBricks && _completedBricks.length) {
        setCompletedBricks(_completedBricks);
      }
      setCompletedBrick(_completedBrick);
      setSkip(stepsSkip);
      consumerTokenStorage.set(JSON.stringify(res.sessionToken));
    } catch (e) {
      console.error(e);
    }
  }

  const splitArray = (array, A, B) => {
    const result = [];
    const rows = array.length;
    const cols = array[0].length;

    for (let i = 0; i < rows; i += A) {
      for (let j = 0; j < cols; j += B) {
        const subArray = [];
        for (let k = 0; k < A; k++) {
          if (i + k < rows) {
            subArray.push(array[i + k].slice(j, j + B));
          }
        }
        result.push(subArray);
      }
    }

    return result;
  }

  const splitMatrix = (array, A) => splitArray(array, A, A);

  useEffect(() => {
    if (!schema || !schema?.length) return;

    let kits = [];

    splitMatrix(schema, 64).forEach(kit => {
      let bases = [];
      splitMatrix(kit, 32).forEach(base => {
        let areas = [];
        splitMatrix(base, 16).forEach(area => {
          areas.push(area);
        });
        bases.push(areas);
      });
      kits.push(bases);
    });
    setParts(kits);
    setKitsQuantity(kits.length);
  }, [schema]);

  useEffect(() => {
    setCompletedBrick(completedBricks[areaIndex]);
    if (!unsavedStatus) return;

    const saveHandler = setTimeout(() => {
      saveData(completedBricks);
      setUnsavedStatus(false);
    }, 500);

    return () => {
      clearTimeout(saveHandler);
    }

  }, [unsavedStatus, completedBricks]);

  useEffect(() => {
    setAreaIndex(activeArea + activeBase * 4 + activeKit * 16);
  }, [activeArea, activeKit]);

  useEffect(() => {
    setActiveArea(0);
    setAreaIndex(activeBase * 4 + activeKit * 16);
  },[activeBase]);

  useEffect(() => {
    resumeData();
  }, [areaIndex]);

  useEffect(() => {
    if (!viewMainRef.current) return;
    setTimeout(() => {
      const rect = viewMainRef.current.getBoundingClientRect();
      setViewMainTop(Math.round(rect.top));
      setViewMainBottom(Math.round(rect.bottom));
    }, 300);
  }, [viewMainRef.current]);

  const onBrickClick = (row: number, column: number) => {
    const index = row * 16 + column + 1;
    if (activeBrick === index) {
      const bricks = [...completedBricks];
      bricks[areaIndex] = activeBrick;
      setSkip(0);
      setCompletedBrick(index);
      setCompletedBricks(bricks);
      setActiveBrick(0);
      setUnsavedStatus(true);
    } else {
      setActiveBrick(index);
    }
  }

  return (
    <Container
      sx={{
        height: '100%',
        maxWidth: '980px',
        marginInline: 'auto',
      }}
      className="assembly-view-container"
    >
      <div className='assembly-view_grid'>
        {
          (kitsQuantity > 1) && (
            <div className='assembly-view_kits'>
              <AssemblyKitSelector kitsQuantity={kitsQuantity} activeKit={activeKit} setActiveKit={setActiveKit}/>
            </div>
          )
        }
        <div className='assembly-view_base'>
          <AssemblyBaseSelector activeBase={activeBase} setActiveBase={setActiveBase}/>
        </div>
        <div className='assembly-view_area'>
          <AssemblyAreaSelector activeSector={activeArea} setActiveSector={setActiveArea}/>
        </div>
        <div className='assembly-view_main' ref={viewMainRef}>
          {parts && parts.length && (
            <AssemblyMainField
              part={parts[activeKit][activeBase][activeArea]}
              onBrickClick={onBrickClick}
              activeBrick={activeBrick}
              completedBrick={completedBrick}
              startX={activeArea === 1 || activeArea === 3 ? 16 : 0}
              startY={activeArea > 1 ? 16 : 0}
            />)}
        </div>
        <div
          className={classNames('assembly-view_preview', {show: showPreview})}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setShowPreview(false)
          }}
        >
          <div
            className="how-to"
            onClick={() => {
              setTutorialVisible(true);
              setMobileTutorialStep(2);
            }}
          >
            <button className="icon">?</button>
            <button>{textData.assemblyViewHelpButton}</button>
          </div>
          <AssemblyPreview
            schema={schema}
            paletteId={paletteId}
            activeBase={activeBase}
            activeArea={activeArea}
            onClick={(e: any) => {
              e.stopPropagation();
              e.preventDefault();
              setShowPreview(!showPreview);
            }}/>
        </div>
        <AssemblyFooter
          onClear={() => {
            setCompletedBrick(0);
          }}
          stepBack={stepBackHandler}
        />
      </div>
      {tutorialVisible && <TutorialLayerComponent onClick={() => {
        setTutorialVisible(false);
      }}/>}
      {mobileTutorialStep >= 0 &&
        <MobileTutorialComponent
          top={viewMainTop}
          bottom={viewMainBottom}
          step={mobileTutorialStep}
          onClick={() => { setMobileTutorialStep(mobileTutorialStep - 1) }}
        />}
    </Container>
  );
};
